// React imports
import React from 'react';

// @mui material components
import {Card, Divider, Grid} from '@mui/material';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import Footer from 'examples/Footer';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';

import {Loading, Notify} from 'notiflix';
import {Controller, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {customerDetailsSchema} from 'schemas/customerDetailsSchema';
import axios from 'axios';
import {yupResolver} from '@hookform/resolvers/yup';
import {ENDPOINT} from 'constants';
import TimezoneSelector from '../../../components/TimeZones';
import CountrySelector from 'components/CountrySelector';
import CurrencySelector from 'components/CurrencySelector';

// Define customer fields dynamically
const customerFields = [
  {name: 'firstName', label: 'First Name'},
  {name: 'lastName', label: 'Last Name'},
  {name: 'email', label: 'Email'},
  {name: 'password', label: 'Password'},
  {name: 'username', label: 'Username'},
  {name: 'countryCode', label: 'Country Code', type: 'autocomplete'},
  {name: 'phoneNumber', label: 'Phone Number'},
  {name: 'timeZone', label: 'Time Zone', type: 'select'},
  {name: 'currency', label: 'Select Currency', type: 'currency'},
  {name: 'city', label: 'City'},
  {name: 'state', label: 'State'},
  {name: 'country', label: 'Select Country', type: 'autocomplete'},
  {name: 'postalCode', label: 'Postal Code'},
  {name: 'streetName', label: 'Street Name'},
  {name: 'houseNumber', label: 'House Number'},
  {name: 'businessName', label: 'Business Name'},
  {name: 'businessCategory', label: 'Business Category'},
  {name: 'businessContactNumber', label: 'Business Contact Number'},
  {name: 'businessWebsiteURL', label: 'Website URL'},
  {name: 'businessTaxNumber', label: 'Tax Number'},
  {
    name: 'businessCountry',
    label: 'Select Business Country',
    type: 'autocomplete',
  },
  {name: 'businessState', label: 'Business State'},
  {name: 'businessCity', label: 'Business City'},
  {name: 'businessStreetName', label: 'Business Street Name'},
  {name: 'businessHouseNumber', label: 'Business House Number'},
  {name: 'businessPostalCode', label: 'Business Postal Code'},
];

const CreateCustomer = () => {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: {errors},
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(customerDetailsSchema),
    defaultValues: customerFields.reduce(
      (acc, field) => {
        acc[field.name] = field.type === 'file' ? null : '';
        return acc;
      },
      {status: 'inactive'},
    ),
  });

  // Handle status change
  const handleStatusChange = event => {
    setValue('status', event.target.checked ? 'active' : 'inactive');
  };

  const handleSave = async data => {
    try {
      const obj = {},
        businessObj = {};

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'businessWebsiteURL') {
          businessObj['websiteURL'] = value;
          delete data[key];
        } else if (key === 'businessTaxNumber') {
          businessObj['taxNumber'] = value;
          delete data[key];
        } else if (key.startsWith('business')) {
          businessObj[key] = value;
        } else if (key === 'timeZone') {
          obj['timeZone'] = value.selectedUtc;
        } else {
          obj[key] = value;
        }
      });

      obj['businessDetail'] = businessObj;

      Loading.circle('Saving...');
      const response = await axios.post(`${ENDPOINT}api/v1/user/signup`, obj);
      Loading.remove();
      Notify.success('Customer details updated successfully');
      setTimeout(() => {
        navigate('/customers');
      }, 1000);
    } catch (error) {
      console.error(error);
      Loading.remove();
      Notify.failure(error);
    }
  };

  // File Input Handling in the Form
  const renderFields = () => (
    <Grid container spacing={2}>
      {customerFields.map(({name, label, type}) => {
        return (
          <Grid item xs={12} sm={6} key={name}>
            <MDBox mb={2}>
              <Controller
                name={name}
                control={control}
                render={({field: {onChange, value}}) =>
                  type === 'file' ? (
                    <>
                      <MDTypography variant="h6" gutterBottom>
                        {label} {/* Show the label for file input */}
                      </MDTypography>
                      {value && (
                        <img
                          src={
                            typeof value === 'string'
                              ? value
                              : URL.createObjectURL(value)
                          }
                          alt="Uploaded Preview"
                          style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                            marginBottom: '10px',
                          }}
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => onChange(e.target.files[0] || null)} // Ensure null when no file is selected
                      />
                      {errors[name] && (
                        <MDTypography variant="caption" color="error">
                          {errors[name].message}
                        </MDTypography>
                      )}
                    </>
                  ) : type === 'autocomplete' ? (
                    <CountrySelector
                      label={label}
                      onChange={onChange}
                      value={value}
                      errors={errors}
                      fieldName={'countryCode'}
                      showCountryCode={name === 'countryCode' ? true : false}
                    />
                  ) : type === 'select' ? (
                    <TimezoneSelector onChange={onChange} />
                  ) : type === 'currency' ? (
                    <CurrencySelector
                      label={label}
                      onChange={onChange}
                      value={value}
                      errors={errors}
                      fieldName={'currency'}
                    />
                  ) : (
                    <MDInput
                      {...{onChange, value}}
                      label={label}
                      fullWidth
                      error={!!errors[name]}
                      helperText={errors[name]?.message}
                      margin="normal"
                    />
                  )
                }
              />
            </MDBox>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={12} sx={{display: 'flex'}}>
            <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
            <Card sx={{margin: 'auto', padding: 3}}>
              <MDBox textAlign="center">
                <MDTypography variant="h4" fontWeight="medium">
                  Create Customer
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3}>
                <form onSubmit={handleSubmit(handleSave)}>
                  {renderFields()}
                  <MDBox mt={4} mb={1}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      type="submit">
                      Save Changes
                    </MDButton>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
            <Divider orientation="vertical" sx={{mx: 0}} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default CreateCustomer;
